import { useCallback } from 'react';
import type { TrackEvent } from './Tracker';
import { activeTracker } from './configure';
import { sendOne } from './sendOne';

/**
 * Returns `{ track, send }`, sending to the tracker `configureTracking` chose (no provider since
 * 2026-10-21). `send` (2026-10-09) resolves once the event has left the page, or
 * after the backend's timeout — await it before a navigation. It never rejects. Safe to call anywhere: if no tracker is configured (or
 * tracking is disabled), `track` is a no-op and never throws.
 *
 * @example
 * ```tsx
 * const { track } = useTracker();
 * track({ name: AppEvents.SAVE_ACTIVITY, category: "Activity", label: title });
 * ```
 */
export function useTracker(): {
  track: (event: TrackEvent) => void;
  send: (event: TrackEvent) => Promise<void>;
} {
  const track = useCallback((event: TrackEvent) => {
    const tracker = activeTracker();
    if (!tracker) return;
    try {
      tracker.track(event);
    } catch (error) {
      // Tracking must never break the app.
      console.error('tracking: track failed', error, event);
    }
  }, []);

  const send = useCallback(async (event: TrackEvent) => {
    const tracker = activeTracker();
    if (!tracker) return;
    try {
      await sendOne(tracker, event);
    } catch (error) {
      console.error('tracking: send failed', error, event);
    }
  }, []);

  return { track, send };
}
